import React, { useEffect, useCallback, memo } from 'react';
import { cn } from "@/lib/utils";
import { EventBlock } from './EventBlock';
import { EventObject } from './types';

interface DayColumnProps {
	timeSlots: Array<{ hour: number; minutes: number }>;
	events: Array<EventObject & { position: number; isVisible: boolean }>;
	onTimeSlotClick: (hour: number, minutes: number) => void;
	calculateEventStyle: (event: EventObject & { position: number; isVisible: boolean }) => React.CSSProperties;
	onEventClick: (event: EventObject) => void;
	userTimezone: string;
	candidateNameMap: Record<string, string>;
}

// Use memo to prevent unnecessary re-renders of the entire column
export const DayColumn = memo(({ 
	timeSlots, 
	events, 
	onTimeSlotClick, 
	calculateEventStyle, 
	onEventClick,
	userTimezone,
	candidateNameMap
}: DayColumnProps) => {
	// Memoized event handler to prevent unnecessary re-renders
	const handleEventClick = useCallback((event: EventObject) => {
		onEventClick(event);
	}, [onEventClick]);
	
	// Memoized time slot click handler
	const handleTimeSlotClick = useCallback((hour: number, minutes: number) => {
		onTimeSlotClick(hour, minutes);
	}, [onTimeSlotClick]);
	
	return (
		<div className="calendar-column border-r relative">
			{timeSlots.map(({ hour, minutes }, index) => (
				<div 
					key={`${hour}-${minutes}`}
					className={cn(
						"h-8 hover:bg-blue-50 cursor-pointer transition-colors relative",
						index === 0
							? "border-t border-gray-200"
							: minutes === 0 
								? "border-t border-gray-200" 
								: "border-t border-dotted border-gray-100"
					)}
					onClick={() => handleTimeSlotClick(hour + (minutes / 60), minutes)}
				/>
			))}
			
			{events
				.filter(event => event.isVisible)
				.map((event) => {
					let candidateName = '';
					if (event.metadata && Array.isArray(event.metadata.candidates) && event.metadata.candidates.length > 0) {
						candidateName = candidateNameMap[event.metadata.candidates[0]] || '';
					}
					return (
						<EventBlock
							key={event.id}
							event={event}
							style={calculateEventStyle(event)}
							onClick={() => handleEventClick(event)}
							userTimezone={userTimezone}
							candidateName={candidateName}
						/>
					);
				})}
		</div>
	);
});

// Add display name for better debugging
DayColumn.displayName = 'DayColumn';
